import React, { useState, useEffect, useRef } from "react";
import { MuiOtpInput } from "mui-one-time-password-input";
import CryptoJS from "crypto-js";

import { verifyOtp, resendOtp } from "../../Actions/AuthActions";
import { CRYPTO_TOKEN_SECRET_KEY } from "../../config";
import { MyArrowBackIcon } from "../../Assets/svg/MyIcons";

const encryptData = (data) => {
	return CryptoJS.AES.encrypt(
		typeof data === "string" ? data : JSON.stringify(data),
		CRYPTO_TOKEN_SECRET_KEY
	).toString();
};

const LoadingSpinner = () => (
	<span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />
);

const VerifyOtp = ({
	email,
	accessToken,
	ip,
	publicSettings,
	showSnackbar,
	navigate,
	updateUserData,
	isResetPassword,
	setIsResetPassword,
	setCurrentScreen,
	SCREENS,
	isEmailVerified,
}) => {
	const otpInputRef = useRef(null);
	const submittingRef = useRef(false);

	const [otp, setOtp] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [buttonDisable, setButtonDisable] = useState(false);
	const [resendOtpLoading, setResendOtpLoading] = useState(false);
	const [timer, setTimer] = useState(120);
	const [isResendDisabled, setIsResendDisabled] = useState(true);

	useEffect(() => {
		const resendOtpTime = publicSettings?.find((s) => s.key === "RESEND_OTP_TIME");
		const timerValue = resendOtpTime ? parseInt(resendOtpTime.value) : 120;
		setTimer(timerValue);
		setIsResendDisabled(true);
	}, [publicSettings]);

	useEffect(() => {
		let interval;
		if (timer > 0) {
			interval = setInterval(() => {
				setTimer((prev) => prev - 1);
			}, 1000);
		} else {
			setIsResendDisabled(false);
		}
		return () => clearInterval(interval);
	}, [timer]);

	const handleSuccessfulLogin = (checkUser) => {
		updateUserData(checkUser);
		localStorage.setItem("userData", encryptData(checkUser));
		localStorage.setItem("GLOBAL_SETTING", encryptData(checkUser?.settingData));

		showSnackbar("Login successful!", "success");

		setTimeout(() => {
			if (!checkUser.isTermsRead) {
				navigate("/T&C");
			} else if (checkUser.isPaasExpiresWarning) {
				localStorage.setItem("passwordExpiry", true);
				localStorage.setItem("passwordExpiryDaysLeft", checkUser.daysLeft);
				navigate("/dashboard");
			} else {
				navigate("/dashboard");
			}
		}, 1000);
	};

	const startOtpTimer = () => {
		const resendOtpTime = publicSettings?.find((s) => s.key === "RESEND_OTP_TIME");
		const timerValue = resendOtpTime ? parseInt(resendOtpTime.value) : 120;
		setTimer(timerValue);
		setIsResendDisabled(true);
	};

	const handleChangeOtp = (newValue) => {
		setOtp(newValue);
		setError("");
	};

	const handleSubmitOtp = async (e, otpValue = otp) => {
		e?.preventDefault?.();

		const code = String(otpValue || "").trim();
		if (code.length !== 6) {
			setError("OTP must be 6 digits.");
			return;
		}

		if (submittingRef.current || loading || buttonDisable) return;

		submittingRef.current = true;
		setButtonDisable(true);
		setLoading(true);
		setError("");

		try {
			const res = await verifyOtp(code, accessToken);

			if (res.data.status === 202 || res.status === 202) {
				showSnackbar(res.data.message || res.message, "error");
				return;
			}

			if (res.data?.data?.checkUser?.twoFactor && !isResetPassword) {
				handleSuccessfulLogin(res.data.data.checkUser);
			} else {
				setIsResetPassword(false);
				setCurrentScreen(SCREENS.RESET_PASSWORD);
				showSnackbar(res.data.message, "success");
			}
		} catch (err) {
			showSnackbar(err.response?.data?.message, "error");
		} finally {
			setOtp("");
			setLoading(false);
			setButtonDisable(false);
			submittingRef.current = false;

			setTimeout(() => {
				const firstInput = document.querySelector(
					'input[aria-label="Please enter OTP character 1"]'
				);
				if (firstInput) firstInput.focus();
			}, 100);
		}
	};

	const handleOtpComplete = (value) => {
		handleSubmitOtp(null, value);
	};

	const handleResendOtp = async () => {
		setIsResendDisabled(true);
		setResendOtpLoading(true);

		try {
			const data = {
				email,
				token: accessToken,
				ip,
			};

			const res = await resendOtp(data);

			if (res.status === 202) {
				showSnackbar(res.data.message, "info");
			} else {
				startOtpTimer();
				showSnackbar(res.data.message, "success");
			}
		} catch (err) {
			showSnackbar(err.response?.data?.message || err.message, "error");
		} finally {
			setResendOtpLoading(false);
		}
	};

	const handleBackToLogin = () => {
		setOtp("");
		setError("");
		setCurrentScreen(SCREENS.LOGIN);
	};

	const getButtonDisabled = () => {
		return buttonDisable || loading || otp.length !== 6;
	};

	return (
		<>
			<div className="fv-row mb-6 fv-plugins-icon-container">
				<MuiOtpInput
					ref={otpInputRef}
					value={otp}
					onChange={handleChangeOtp}
					onComplete={handleOtpComplete}
					length={6}
					validateChar={(char) => !isNaN(char)}
					TextFieldsProps={{
						type: "tel",
						inputMode: "numeric",
						pattern: "[0-9]*",
						disabled: loading || buttonDisable,
					}}
				/>
			</div>

			{error && <p className="text-danger fs-6">{error}</p>}

			<div className="d-grid">
				<button
					onClick={handleSubmitOtp}
					className="btn btn-primary fs-3 h-50px"
					disabled={getButtonDisabled()}
				>
					Verify OTP
					{loading && <LoadingSpinner />}
				</button>

				<div className="text-center mt-8 fs-6">
					{timer > 0 ? (
						<span className="text-muted">Resend OTP in {timer} seconds</span>
					) : (
						<a
							href="#"
							className="text-primary text-decoration-none link-hover fw-bold"
							onClick={(e) => {
								e.preventDefault();
								handleResendOtp();
							}}
						>
							{resendOtpLoading ? "Sending..." : "Resend OTP"}
						</a>
					)}
				</div>

				<div className="text-center mt-6 pe-3">
					<button
                        type="button"
                        onClick={handleBackToLogin}
                        className="back-to-login-btn btn btn-link text-decoration-none fw-bold fs-5 text-primary cursor-pointer position-relative p-0 border-0 bg-transparent align-items-center"
                    >
                        <MyArrowBackIcon className="back-arrow me-2" />
                        <span>Back to Login</span>
                    </button>
				</div>
			</div>
		</>
	);
};

export default VerifyOtp;